import { FunctionComponent, memo } from "react";
import Modal, { ModalContent, ModalHeader, useModal } from "@edgetier/modal";
import { Button } from "@edgetier/components";
import { faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import { IProps } from "./users-subscribe-button.types";
import UsersSubscribedForm from "../users-subscribed-form";
import { getTimeToRead } from "@edgetier/utilities";
import Axios from "axios";
import { useQueryClient } from "react-query";
import { axios } from "utilities/axios";
import { Route } from "@edgetier/watchtower-types";
import toast from "react-hot-toast";
import { getServerError } from "utilities/get-server-error";
import { postTagModel } from "./users-subscribe-button.utilities";
import "./users-subscribe-button.scss";

/**
 * A button that, when clicked, opens the form in a modal to subscribe one or more users.
 * @param props.modelId
 */
const UsersSubscribeButton: FunctionComponent<IProps> = ({ onCreateModel, modelId: initialModelId, tagId }) => {
    const queryClient = useQueryClient();
    const { openModal, closeModal, modalProps } = useModal();

    /**
     * Create a tag model if one doesn't exist.
     * @param userIds Users to subscribe.
     */
    const onSubscribe = async (userIds: string[]) => {
        // Either use the existing model or create a new one.
        const modelId = typeof initialModelId === "string" ? initialModelId : await postTagModel(tagId);

        if (typeof modelId !== "string") {
            toast.error("Subscription failed");
            return;
        }

        try {
            const subscribePromises =
                userIds.length > 0
                    ? userIds.map((userId) => axios.post(Route.UserSubscribe.replace(":userId", userId), { modelId }))
                    : [];
            const responses = await Promise.all(subscribePromises.map((promise) => promise.catch((error) => error)));

            const errors = responses.filter((element) => element.name === "Error");
            const successes = userIds.length - errors.length;

            if (errors.length > 0) {
                const errorMessage =
                    errors.length === 1 ? "Failed to subscribe a user" : "Failed to subscribe some users";

                toast.error(errorMessage, { duration: getTimeToRead(errorMessage, 1000) });
            }
            if (successes > 0) {
                const successMessage =
                    successes === 1
                        ? `Subscribed user to receive alerts`
                        : `Subscribed ${userIds.length} users to receive alerts`;
                toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
                queryClient.invalidateQueries(Route.ModelSubscriptions.replace(":modelId", modelId));
            }
        } catch (error) {
            if (Axios.isAxiosError(error)) {
                toast.error(getServerError(error));
            }
        } finally {
            if (typeof initialModelId !== "string" && typeof modelId === "string") {
                onCreateModel(modelId);
            }
            closeModal();
        }
    };
    return (
        <>
            <Modal {...modalProps}>
                <ModalHeader>
                    <h2>Subscribe Users</h2>
                </ModalHeader>
                <ModalContent>
                    <UsersSubscribedForm onSubscribe={onSubscribe} />
                </ModalContent>
            </Modal>
            <Button icon={faUserPlus} onClick={openModal} className="users-subscribe-button">
                Subscribe Users
            </Button>
        </>
    );
};

export default memo(UsersSubscribeButton);
